import { Grid } from '@mui/material';
import { RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import GoalCustomerCommunication from '../../../assets/svg/onboarding/icons/GoalCustomerCommunication';
import GoalRoutesPlanning from '../../../assets/svg/onboarding/icons/GoalRoutesPlanning';
import GoalRoutesTracking from '../../../assets/svg/onboarding/icons/GoalRoutesTracking';
import Other from '../../../assets/svg/onboarding/icons/Other';
import { useUpdateOrganizationOnboarding } from '../../../hooks/organization/useUpdateOrganizationOnboarding';
import Button from '../../../react-components/Button';
import RoutalInputText from '../../../react-components/InputText';
import Text from '../../../react-components/Text';
import eventsPanel from '../../../services/events';
import { AnimatedStep } from './AnimatedStep';

const goals = [`routes_planning`, `routes_tracking`, `customer_communication`, `other`];

interface GoalProps {
  onNextStep: () => void;
}

export const Goal = ({ onNextStep }: GoalProps) => {
  const { success, run: updateOrganization } = useUpdateOrganizationOnboarding();
  const [goal, setGoal] = useState<string[]>([]);
  const [other, setOther] = useState<string>(``);

  useEffect(() => {
    if (success) {
      setTimeout(() => {
        onNextStep();
        eventsPanel.onboardingPageView({ page: `goal`, data: { goal } });
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  const isGoalActive = (g: string) => goal.includes(g);

  const isNextButtonEnabled = () => {
    if (goal.length === 0) {
      return false;
    }

    if (goal.length === 1 && goal.includes(`other`) && other === ``) {
      return false;
    }

    return true;
  };

  const getIconByGoal = (g: string) => {
    switch (g) {
      case `routes_planning`:
        return <GoalRoutesPlanning color={isGoalActive(g) ? RoutalPalette.primary40 : RoutalPalette.text} />;
      case `routes_tracking`:
        return <GoalRoutesTracking color={isGoalActive(g) ? RoutalPalette.primary40 : RoutalPalette.text} />;
      case `customer_communication`:
        return <GoalCustomerCommunication color={isGoalActive(g) ? RoutalPalette.primary40 : RoutalPalette.text} />;
      case `other`:
        return <Other color={isGoalActive(g) ? RoutalPalette.primary40 : RoutalPalette.text} />;
      default:
        return null;
    }
  };

  return (
    <AnimatedStep id="goalAnimatedContainer" isVisible={!success}>
      <Grid
        container
        direction="column"
        wrap="nowrap"
        style={{ padding: `20px`, maxWidth: `660px`, position: `relative` }}
      >
        <Grid item>
          <Text variant="h6" intlId="company.onboarding.goal.info" />
        </Grid>

        <VSpacer medium />

        <Grid item container direction="column" wrap="nowrap">
          {goals.map((g, index) => (
            <Grid
              key={g}
              item
              container
              direction="row"
              wrap="nowrap"
              alignItems="center"
              style={{
                cursor: `pointer`,
                borderRadius: `10px`,
                padding: `12px`,
                height: isGoalActive(g) && g === `other` ? `97px` : `76px`,
                border: isGoalActive(g)
                  ? `2px solid ${RoutalPalette.primary40}`
                  : `1px solid ${RoutalPalette.neutral10}`,
                marginBottom: index < goals.length - 1 ? `12px` : `0px`,
              }}
              onClick={() => {
                if (isGoalActive(g)) {
                  setGoal(goal.filter((goal) => goal !== g));
                  if (g === `other`) {
                    setOther(``);
                  }
                } else {
                  setGoal([...goal, g]);
                }
              }}
            >
              <Grid item container direction="row" style={{ width: `unset`, marginRight: `20px` }}>
                {getIconByGoal(g)}
              </Grid>

              <Grid item container direction="column" wrap="nowrap">
                <Grid item>
                  <Text
                    intlId={`company.onboarding.goal.option.${g}`}
                    style={{
                      cursor: `pointer`,
                      color: isGoalActive(g) ? RoutalPalette.primary40 : RoutalPalette.text,
                    }}
                  />
                </Grid>

                {isGoalActive(g) && (
                  <>
                    <Grid item>
                      <VSpacer small />
                    </Grid>

                    <Grid item>
                      {g !== `other` ? (
                        <Text
                          variant="label"
                          intlId={`company.onboarding.goal.option.${g}.info`}
                          style={{ cursor: `pointer`, marginRight: `20px` }}
                        />
                      ) : (
                        <RoutalInputText
                          onClickStopPropagation
                          fieldValue={other}
                          placeholderIntlId="none"
                          inputContainerStyles={{ width: `100%` }}
                          onChange={(text: any) => {
                            setOther(text);
                            if ((text ?? ``) !== `` && !isGoalActive(`other`)) {
                              setGoal([...goal, `other`]);
                            }
                          }}
                        />
                      )}
                    </Grid>
                  </>
                )}
              </Grid>
            </Grid>
          ))}
        </Grid>

        <Grid
          item
          container
          direction="row"
          wrap="nowrap"
          alignItems="center"
          justifyContent="center"
          style={{ position: `absolute`, bottom: `-60px` }}
        >
          {isNextButtonEnabled() && (
            <Button
              intlId="company.onboarding.next"
              style={{
                width: `200px`,
                height: `32px`,
              }}
              onClick={() => {
                const newGoals = goal.includes(`other`) ? [...goal.filter((g) => g !== `other`), other] : goal;
                updateOrganization({
                  goal: newGoals.filter((g) => g !== ``),
                });
              }}
            />
          )}
        </Grid>
      </Grid>
    </AnimatedStep>
  );
};
